import { useState, useRef, useEffect } from 'react'
import { HiChevronDown, HiCheck, HiOfficeBuilding, HiX } from 'react-icons/hi'
import { FaUsers } from 'react-icons/fa'
import { cn } from '../../lib/utils'
import type { AuthorizerOption } from './types'

interface AuthorizerMultiSelectProps {
  options: AuthorizerOption[]
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

export const AuthorizerMultiSelect = ({
  options,
  value,
  onChange,
  placeholder = 'Selecione autorizadores...',
  disabled = false,
  className = ''
}: AuthorizerMultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focar input ao abrir
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen])

  // Filtrar opções baseado na busca
  const filteredOptions = options.filter(option =>
    option.nome_setor.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Separar por tipo
  const setores = filteredOptions.filter(opt => opt.tipo === 'setor')
  const equipes = filteredOptions.filter(opt => opt.tipo === 'equipe')

  // Toggle seleção
  const handleToggle = (optionValue: string | number) => {
    const valueStr = String(optionValue)
    if (value.includes(valueStr)) {
      onChange(value.filter(v => v !== valueStr))
    } else {
      onChange([...value, valueStr])
    }
  }

  // Limpar seleção
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange([])
  }

  // Obter labels selecionados
  const selectedLabels = options
    .filter(opt => value.includes(String(opt.value)))
    .map(opt => opt.nome_setor)

  const renderOption = (option: AuthorizerOption) => {
    const isSelected = value.includes(String(option.value))
    const Icon = option.tipo === 'setor' ? HiOfficeBuilding : FaUsers

    return (
      <div
        key={String(option.value)}
        onClick={() => handleToggle(option.value)}
        className={cn(
          'flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors',
          'hover:bg-gray-100 dark:hover:bg-neutral-700',
          isSelected && 'bg-blue-50 dark:bg-blue-900/20'
        )}
      >
        {/* Checkbox */}
        <div
          className={cn(
            'w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors',
            isSelected
              ? 'bg-blue-600 border-blue-600 dark:bg-blue-500 dark:border-blue-500'
              : 'border-gray-300 dark:border-neutral-600'
          )}
        >
          {isSelected && <HiCheck className="w-4 h-4 text-white" />}
        </div>

        {/* Ícone do tipo */}
        <Icon
          className={cn(
            'w-6 h-6 p-1 rounded-full flex-shrink-0',
            option.tipo === 'setor'
              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
              : 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
          )}
        />

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {option.nome_setor}
            </p>
            <span
              className={cn(
                'text-xs px-1.5 py-0.5 rounded flex-shrink-0',
                option.tipo === 'setor'
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                  : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
              )}
            >
              {option.tipo === 'setor' ? 'Setor' : 'Equipe'}
            </span>
          </div>
          {option.chefe && (
            <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
              Chefe: {option.chefe.name}
            </p>
          )}
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'w-full px-3 py-2 text-left bg-white dark:bg-neutral-800 border rounded-lg',
          'border-gray-300 dark:border-neutral-600',
          'focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400',
          'transition-colors',
          disabled && 'opacity-50 cursor-not-allowed',
          !disabled && 'hover:border-gray-400 dark:hover:border-neutral-500'
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1 min-w-0">
            {value.length === 0 ? (
              <span className="text-gray-500 dark:text-gray-400">{placeholder}</span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {selectedLabels.slice(0, 2).map((label, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm"
                  >
                    {label}
                  </span>
                ))}
                {selectedLabels.length > 2 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 dark:bg-neutral-700 text-gray-700 dark:text-gray-300 text-sm">
                    +{selectedLabels.length - 2}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {value.length > 0 && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded transition-colors"
                title="Limpar seleção"
              >
                <HiX className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </button>
            )}
            <HiChevronDown
              className={cn(
                'w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform',
                isOpen && 'transform rotate-180'
              )}
            />
          </div>
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-neutral-800 border border-gray-300 dark:border-neutral-600 rounded-lg shadow-lg max-h-80 overflow-hidden flex flex-col">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200 dark:border-neutral-700">
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Buscar autorizador..."
              className="w-full px-3 py-2 bg-gray-50 dark:bg-neutral-700 border border-gray-300 dark:border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 text-sm"
            />
          </div>

          {/* Options List */}
          <div className="overflow-y-auto flex-1">
            {filteredOptions.length === 0 ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                Nenhum autorizador encontrado
              </div>
            ) : (
              <>
                {/* Setores */}
                {setores.length > 0 && (
                  <div>
                    <div className="px-3 py-2 bg-gray-50 dark:bg-neutral-700/50 sticky top-0">
                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                        Setores ({setores.length})
                      </p>
                    </div>
                    {setores.map(renderOption)}
                  </div>
                )}

                {/* Equipes */}
                {equipes.length > 0 && (
                  <div>
                    <div className="px-3 py-2 bg-gray-50 dark:bg-neutral-700/50 sticky top-0">
                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                        Equipes ({equipes.length})
                      </p>
                    </div>
                    {equipes.map(renderOption)}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer com contagem */}
          {value.length > 0 && (
            <div className="px-3 py-2 border-t border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-700/50">
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {value.length} autorizador{value.length !== 1 ? 'es' : ''} selecionado
                {value.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
